import { useEffect, useState } from "react";
import { useI18n } from "../../i18n/I18nProvider";
import { disablePush, enablePush, pushKey, pushState, type PushState } from "../../lib/push";

/** Mi cuenta › notifications on this device (purchases, sales, chat messages, offers). */
export function PushSettings() {
  const { t } = useI18n();
  const [key, setKey] = useState<string | null>(null);
  const [state, setState] = useState<PushState | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    let alive = true;
    void Promise.all([pushKey(), pushState()]).then(([k, s]) => {
      if (!alive) return;
      setKey(k);
      setState(s);
    });
    return () => {
      alive = false;
    };
  }, []);

  // Push is off on the server, or this browser can't do it at all: nothing to offer.
  if (!key || !state || state === "unsupported") return null;

  const run = async (fn: () => Promise<PushState | void>) => {
    setBusy(true);
    setError(false);
    try {
      setState((await fn()) ?? (await pushState()));
    } catch {
      setError(true);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="push-settings" aria-labelledby="push-title">
      <h2 id="push-title">{t("push.title")}</h2>
      <p className="muted small">{t("push.intro")}</p>
      {state === "ios-install" && <p className="notice">{t("push.iosInstall")}</p>}
      {state === "denied" && <p className="notice notice-warn">{t("push.denied")}</p>}
      {state === "off" && (
        <button type="button" className="btn btn-primary" disabled={busy} onClick={() => run(() => enablePush(key))}>
          🔔 {t("push.enable")}
        </button>
      )}
      {state === "on" && (
        <p>
          ✅ {t("push.on")}{" "}
          <button type="button" className="btn btn-link btn-sm" disabled={busy} onClick={() => run(async () => (await disablePush(), "off"))}>
            {t("push.disable")}
          </button>
        </p>
      )}
      {error && <p className="form-error">{t("error.generic")}</p>}
    </section>
  );
}
